import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import { useFramework } from '../../../contexts/FrameworkContext';
import { Framework } from '../../../types/frameworks';
import FrameworkBuilder from './FrameworkBuilder';
// BAB-61: PMEConfigurator (the v2.2 8-state rule-priority toggler) is deleted.
// Replaced by DisciplineSection which configures the v3.0 engine via 3
// intent tabs × 6 thresholds (no rule toggling — the engine's priority
// order is fixed by design: hard stops → profit → defensive → hold).
import DisciplineSection from './DisciplineSection';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const FrameworksSettings: React.FC = () => {
  const { activeScoringFramework, setActiveScoringFramework, refreshFrameworks } = useFramework();
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  // BAB-61: pmeFrameworks state removed — the v2.2 framework_pme_rules path
  // is deprecated. Discipline thresholds live in user_discipline_thresholds
  // and are managed by DisciplineSection (which calls its own endpoints).
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingFramework, setEditingFramework] = useState<Framework | null>(null);

  const loadFrameworks = useCallback(async () => {
    setLoading(true);
    try {
      // BAB-61: only the scoring framework load survives. PME rule loading
      // dropped along with the configurator.
      const scoringRes = await apiService.getFrameworks('scoring');
      setFrameworks(scoringRes.frameworks || []);
    } catch (err) {
      console.error('Failed to load frameworks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFrameworks();
  }, [loadFrameworks]);

  const handleActivate = async (fw: Framework) => {
    try {
      await apiService.activateFramework(fw.id);
      setActiveScoringFramework(fw);
      await loadFrameworks();
      await refreshFrameworks();
    } catch (err) {
      console.error('Failed to activate framework:', err);
    }
  };

  const handleDeactivate = async (fw: Framework) => {
    try {
      await apiService.deactivateFramework(fw.id);
      setActiveScoringFramework(null);
      await loadFrameworks();
      await refreshFrameworks();
    } catch (err) {
      console.error('Failed to deactivate framework:', err);
    }
  };

  const handleDelete = async (fw: Framework) => {
    if (!window.confirm(`Delete framework "${fw.name}"?`)) return;
    try {
      await apiService.deleteFramework(fw.id);
      if (activeScoringFramework?.id === fw.id) {
        setActiveScoringFramework(null);
      }
      await loadFrameworks();
      await refreshFrameworks();
    } catch (err) {
      console.error('Failed to delete framework:', err);
    }
  };

  const handleClone = async (fw: Framework) => {
    try {
      await apiService.cloneFramework(fw.id);
      await loadFrameworks();
      await refreshFrameworks();
    } catch (err) {
      console.error('Failed to clone framework:', err);
    }
  };

  const handleEdit = (fw: Framework) => {
    setEditingFramework(fw);
    setShowBuilder(true);
  };

  const handleCreate = () => {
    setEditingFramework(null);
    setShowBuilder(true);
  };

  const handleBuilderClose = async () => {
    setShowBuilder(false);
    setEditingFramework(null);
    await loadFrameworks();
    await refreshFrameworks();
  };

  if (showBuilder) {
    return <FrameworkBuilder framework={editingFramework} onClose={handleBuilderClose} />;
  }

  // BAB-61: showPMEBuilder branch removed — PMEConfigurator deleted.

  const systemFrameworks = frameworks.filter(f => f.user_id === null);
  const userFrameworks = frameworks.filter(f => f.user_id !== null);

  return (
    <div className="frameworks-settings">
      <div className="frameworks-header">
        <div>
          <h2>Stock Scoring Framework</h2>
          <p>Build custom scoring frameworks to rank stocks for covered calls.</p>
        </div>
        <button className="fw-btn-primary" onClick={handleCreate}>
          <PlusIcon className="st-ico" />
          New Framework
        </button>
      </div>

      {loading ? (
        <div className="st-muted-block">Loading...</div>
      ) : (
        <>
          {userFrameworks.length > 0 && (
            <div className="st-fw-group">
              <h3 className="frameworks-section-label">Your Frameworks</h3>
              <div className="st-fw-list">
                {userFrameworks.map(fw => (
                  <FrameworkCard
                    key={fw.id}
                    framework={fw}
                    isActive={activeScoringFramework?.id === fw.id}
                    isSystem={false}
                    onActivate={() => handleActivate(fw)}
                    onDeactivate={() => handleDeactivate(fw)}
                    onEdit={() => handleEdit(fw)}
                    onDelete={() => handleDelete(fw)}
                    onClone={() => handleClone(fw)}
                  />
                ))}
              </div>
            </div>
          )}

          {systemFrameworks.length > 0 && (
            <div className="st-fw-group">
              <h3 className="frameworks-section-label">System Templates</h3>
              <div className="st-fw-list">
                {systemFrameworks.map(fw => (
                  <FrameworkCard
                    key={fw.id}
                    framework={fw}
                    isActive={false}
                    isSystem={true}
                    onClone={() => handleClone(fw)}
                  />
                ))}
              </div>
            </div>
          )}

          {userFrameworks.length === 0 && systemFrameworks.length === 0 && (
            <div className="frameworks-empty">
              <p>No frameworks yet. Create one to customize stock scoring.</p>
            </div>
          )}
        </>
      )}

      {/* Discipline Section — BAB-61
          Replaces the v2.2 PMEConfigurator surface (the 8-state rule-priority
          toggler that has been deleted). The Discipline engine (pme/v3_0.py)
          is configured via 3 intent tabs × 6 thresholds, not rule toggles. */}
      <div className="st-discipline-wrap">
        <DisciplineSection />
      </div>
    </div>
  );
};

// =============================================================================
// FRAMEWORK CARD
// =============================================================================

interface FrameworkCardProps {
  framework: Framework;
  isActive: boolean;
  isSystem: boolean;
  onActivate?: () => void;
  onDeactivate?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onClone?: () => void;
}

const FrameworkCard: React.FC<FrameworkCardProps> = ({
  framework, isActive, isSystem, onActivate, onDeactivate, onEdit, onDelete, onClone,
}) => {
  const qualityIndicators = framework.indicators.filter(i => i.dimension === 'quality');
  const timingIndicators = framework.indicators.filter(i => i.dimension === 'timing');
  const enabledModules = framework.premium_modules.filter(pm => pm.enabled);

  return (
    <div className={`framework-card ${isActive ? 'framework-card--active' : ''}`}>
      {/* Top row: name + badges left, action icons right */}
      <div className="st-fw-top">
        <div className="st-fw-main">
          <div className="st-fw-title">
            <span className="framework-card__name">{framework.name}</span>
            {isActive && (
              <span className="framework-card__badge framework-card__badge--active">
                <CheckCircleSolid className="st-fw-badge-ico" />
                ACTIVE
              </span>
            )}
            {isSystem && (
              <span className="framework-card__badge framework-card__badge--system">SYSTEM</span>
            )}
          </div>

          {framework.description && (
            <p className="st-fw-desc">
              {framework.description}
            </p>
          )}

          {framework.framework_type === 'scoring' && (
            <div className="framework-card__meta">
              <span>Quality: {framework.quality_points}pts ({qualityIndicators.length} indicators)</span>
              <span>Timing: {framework.timing_points}pts ({timingIndicators.length} indicators{enabledModules.length > 0 ? ` + ${enabledModules.length} module${enabledModules.length > 1 ? 's' : ''}` : ''})</span>
            </div>
          )}

          {/* BAB-61: framework_type='pme' branch removed. v2.2 PME framework
              cards no longer render — the Discipline framework (v3.0) is
              configured per-user, per-intent via DisciplineSection above,
              not via cloneable framework rows. */}
        </div>

        {/* Action icons: Edit, Clone, Delete — top right */}
        <div className="st-fw-actions">
          {!isSystem && (
            <>
              <button className="fw-btn-icon" title="Edit" onClick={onEdit}>
                <PencilIcon className="st-ico" />
              </button>
              <button className="fw-btn-icon fw-btn-icon--danger" title="Delete" onClick={onDelete}>
                <TrashIcon className="st-ico" />
              </button>
            </>
          )}
          <button className="fw-btn-icon" title="Clone" onClick={onClone}>
            <DocumentDuplicateIcon className="st-ico" />
          </button>
        </div>
      </div>

      {/* Bottom row: Activate / Deactivate button */}
      {!isSystem && (
        <div className="st-fw-foot">
          {isActive ? (
            <button className="fw-btn-secondary fw-btn--sm" onClick={onDeactivate}>
              Deactivate
            </button>
          ) : (
            <button className="fw-btn-primary fw-btn--sm" onClick={onActivate}>
              Activate
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FrameworksSettings;
